import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  PaginationState,
  useReactTable,
  getCoreRowModel,
  ColumnDef,
  SortingState,
} from '@tanstack/react-table';
import {
  FiFilter,
  FiArchive,
  FiInbox,
  FiZap,
  FiLoader,
  FiCpu,
  FiRefreshCw,
  FiCheckCircle,
  FiCopy,
  FiInfo,
  FiSearch,
  FiX,
  FiSettings,
  FiSend,
  FiFileText,
} from 'react-icons/fi';
import cn from 'classnames';
import { useDebouncedCallback } from 'use-debounce';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import i18n from '@ohif/i18n';
import { messageI18n } from '../../../helpers/i18n/toastI18n';

// --- Your Custom Components ---
import { Button } from '../../ComponnentsUI/common/Button';
import { Input } from '../../ComponnentsUI/common/Input';
import { Table } from '../../ComponnentsUI/common/Table';
import { DateInput } from '../../ComponnentsUI/common/DateInput';

// --- Your API Hooks & Utils ---
import {
  usePaginatedExams,
  useArchiveExam,
  useUnarchiveExam,
  type ExamListCreator,
  type ExamListItem,
  type ExamListPredictionState,
  type ExamListSeries,
} from '../../../helpers/api/studies/studies';
import { usePredictSerie } from '../../../helpers/api/instance/instance';
import {
  useGetGeneralViewerInstances,
  useGetInstanceFramesMetadata,
  fetchFrameImage,
  type GeneralViewerInstance,
} from '../../../helpers/api/view_frame/view-frames';

// --- Existing Modals & Components ---
import { NewCaseReverseUpload } from '../../ComponnentsUI/NewCaseReverseUpload/NewCaseReverseUpload';
import { usePermissions } from '../../../helpers/acl/permissions';
import { CompleteIncompleteUploadModal } from '../../ComponnentsUI/CompleteIncompleteUploadModal/CompleteIncompleteUploadModal';
import {
  getUploadSessionByStudy,
  type UploadSessionsByStudy,
} from '../../../helpers/api/UnifiedUpload/unified';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useGlobalWebSocket } from '../../../state/GlobalContextProvider';
import { ServerSelectionModal } from '../../ComponnentsUI/ServerSelectionModal/ServerSelectionModal';
import { computeGAFromLmp } from '../../ComponnentsUI/NewCaseReverseUpload/utils';
import GeneralViewerDicomMetadataPanel, {
  type DicomMetadataPanelOpenPayload,
} from '../../Components/GeneralViewer/GeneralViewerDicomMetadataPanel';
import { ViewerToolCluster } from '../../Components/GeneralViewer/ViewerToolCluster';

// --- Helper Functions ---
const getDayjsLocale = () => {
  const lang = i18n.resolvedLanguage || i18n.language || 'en-US';
  return lang.startsWith('fr') ? 'fr' : 'en';
};

const formatDate = (dateString?: string) => {
  if (!dateString) {
    return i18n.t('common.notAvailable', { ns: 'ExamManagement' });
  }
  const date = dayjs(
    dateString.includes('T')
      ? dateString
      : dateString.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')
  ).locale(getDayjsLocale());
  return date.isValid()
    ? date.format('D MMMM YYYY')
    : i18n.t('common.invalidDate', { ns: 'ExamManagement' });
};

const formatGestationalAge = (weeks: number, days: number) =>
  i18n.t('pregnancy.gaShort', {
    ns: 'ExamManagement',
    weeks,
    days: Number.isNaN(days) ? 0 : days,
  });

const parseFlexibleDate = (dateString?: string | null) => {
  if (!dateString) return dayjs(null);
  const raw = String(dateString).trim();
  if (!raw) return dayjs(null);
  if (raw.includes('T')) return dayjs(raw);
  if (/^\d{8}$/.test(raw)) {
    return dayjs(raw.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
  }
  return dayjs(raw);
};

/** Case record time: prefer `created_at` (when the exam was created in the system). */
const formatExamDateTime = (exam: {
  study_date?: string | null;
  created_at?: string | null;
}) => {
  const locale = getDayjsLocale();
  const created = exam.created_at
    ? dayjs(exam.created_at).locale(locale)
    : dayjs(null);
  if (created.isValid()) {
    return (
      <span className="text-xs font-medium text-slate-700 dark:text-slate-200 whitespace-nowrap">
        {created.format('D MMM YYYY · HH:mm')}
      </span>
    );
  }
  const study = parseFlexibleDate(exam.study_date ?? undefined).locale(locale);
  if (study.isValid()) {
    return (
      <span className="text-xs font-medium text-slate-700 dark:text-slate-200 whitespace-nowrap">
        {study.format('D MMM YYYY · HH:mm')}
      </span>
    );
  }
  return <span className="text-slate-400">—</span>;
};

type StudyCreatorInfo = ExamListCreator;

const formatCreatorLabel = (creator: StudyCreatorInfo | null | undefined) => {
  if (!creator) return '—';
  const name = [creator.first_name, creator.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (name) return name;
  return creator.email || '—';
};

const copyToClipboard = (text: string) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      messageI18n.success('exam.copiedToClipboard');
    })
    .catch((err) => {
      console.error('Failed to copy text: ', err);
      messageI18n.error('exam.copyFailed');
    });
};

const calculateAge = (birthDate?: string) => {
  if (!birthDate) {
    return i18n.t('common.notAvailable', { ns: 'ExamManagement' });
  }
  const birth = dayjs(
    birthDate.includes('T')
      ? birthDate
      : birthDate.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')
  );
  if (!birth.isValid()) {
    return i18n.t('common.invalidDate', { ns: 'ExamManagement' });
  }
  const years = dayjs().diff(birth, 'years');
  return i18n.t('common.ageYearsShort', { ns: 'ExamManagement', years });
};

const calculateGA = (lmpDate?: string) => {
  if (!lmpDate) return 'N/A';
  const lmp = dayjs(
    lmpDate.includes('T')
      ? lmpDate
      : lmpDate.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')
  );
  if (!lmp.isValid()) return 'Invalid';
  const totalDays = dayjs().diff(lmp, 'days');
  const weeks = Math.floor(totalDays / 7);
  const days = totalDays % 7;
  return `${weeks}w ${days}d`;
};

// ─── Exam Image Strip ─────────────────────────────────────────────────────────

const FRAMES_PAGE = 30;

/** Borderless icon hits in the Actions column (color = role). */
const EXAM_ICON_HIT =
  'h-9 w-9 inline-flex items-center justify-center rounded-lg border-0 bg-transparent shrink-0 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-slate-900';

/** DICOM + export icon buttons for the exam table Actions column (instances loaded per row). */
function ExamRowDicomExportIcons({
  studyUID,
  onOpenStudyDicom,
}: {
  studyUID: string;
  onOpenStudyDicom: () => void;
}) {
  const { data } = useGetGeneralViewerInstances(
    studyUID,
    undefined,
    undefined,
    {
      enabled: !!studyUID,
    }
  );
  const instances: GeneralViewerInstance[] = useMemo(() => {
    const d = data as
      | { data?: { data?: unknown }; data?: unknown }
      | unknown[]
      | undefined;
    const raw =
      d && typeof d === 'object' && !Array.isArray(d)
        ? ((d as { data?: { data?: unknown[] } }).data?.data ??
          (d as { data?: unknown }).data)
        : Array.isArray(d)
          ? d
          : [];
    return Array.isArray(raw) ? raw : [];
  }, [data]);
  return (
    <ViewerToolCluster
      studyUID={studyUID}
      variant="light"
      dicomActive={false}
      onDicomClick={onOpenStudyDicom}
      dicomInstances={instances}
      liveExport={null}
      actionsIconOnly
      actionsIconGhost
      examExportMenu
    />
  );
}

/** Single lazily-loaded frame thumbnail — only fetches image when visible in the scroll container */
const FrameThumb: React.FC<{
  imageUrl: string;
  frameNumber: number;
  scrollRoot: HTMLDivElement | null;
  onClick: (e: React.MouseEvent) => void;
  /** Opens instance-level DICOM metadata (same tags for all frames in the clip). */
  onDicomClick?: (e: React.MouseEvent) => void;
}> = ({ imageUrl, frameNumber, scrollRoot, onClick, onDicomClick }) => {
  const { t } = useTranslation('ExamManagement');
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!imageUrl) return;
    const ctrl = new AbortController();
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          setLoading(true);
          fetchFrameImage(imageUrl, { signal: ctrl.signal })
            .then((url) => {
              if (url) setBlobUrl(url);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
        }
      },
      { root: scrollRoot, rootMargin: '0px 200px 0px 0px', threshold: 0 }
    );
    if (divRef.current) observer.observe(divRef.current);
    return () => {
      observer.disconnect();
      ctrl.abort();
    };
  }, [imageUrl, scrollRoot]);

  return (
    <div
      ref={divRef}
      onClick={onClick}
      title={`Frame ${frameNumber + 1} — click to open in viewer`}
      className="relative flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden cursor-pointer group
                 border border-slate-200 hover:border-blue-400 hover:shadow-md transition-all bg-slate-100"
    >
      {blobUrl ? (
        <img
          src={blobUrl}
          alt={`Frame ${frameNumber + 1}`}
          className="w-full h-full object-contain bg-black"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-slate-200">
          {loading && (
            <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          )}
        </div>
      )}
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-start p-1">
        <span className="text-white text-[9px] font-bold">
          {frameNumber + 1}
        </span>
      </div>
      {onDicomClick && (
        <button
          type="button"
          className="absolute top-0.5 right-0.5 z-10 w-5 h-5 rounded-md bg-black/60 text-white flex items-center justify-center hover:bg-sky-600 transition-colors shadow-sm"
          title={t('strip.dicomFrameHint')}
          onClick={(e) => {
            e.stopPropagation();
            onDicomClick(e);
          }}
        >
          <FiInfo className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

/** Frames for a single instance — batched rendering, loads more on demand */
const InstanceFramesRow: React.FC<{
  studyUID: string;
  instance: GeneralViewerInstance;
  scrollRoot: HTMLDivElement | null;
  /** Called with the viewer-instance UID and the frame's DB primary key (frame_id) */
  onNavigateToFrame: (instanceSopUID: string, frameId: string) => void;
  onNavigate: () => void;
  onOpenInstanceDicom?: (instance: GeneralViewerInstance) => void;
}> = ({
  studyUID,
  instance,
  scrollRoot,
  onNavigateToFrame,
  onNavigate,
  onOpenInstanceDicom,
}) => {
  const { t } = useTranslation('ExamManagement');
  const { data, isLoading } = useGetInstanceFramesMetadata(
    studyUID,
    instance.sop_instance_uid
  );
  const [visibleCount, setVisibleCount] = useState(FRAMES_PAGE);

  const allFrames: any[] = useMemo(() => {
    const raw =
      (data as any)?.data?.data?.frames ?? (data as any)?.data?.frames ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [data]);

  const frames = allFrames.slice(0, visibleCount);
  const remaining = allFrames.length - visibleCount;
  const isVideo = instance.format_file === 'VIDEO';

  return (
    <div className="flex flex-col gap-1 flex-shrink-0">
      {/* Instance label */}
      <div className="flex items-center gap-1.5 px-1 flex-wrap">
        {isVideo && (
          <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[8px] font-bold rounded">
            VIDEO
          </span>
        )}
        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide">
          {instance.frame_count} frames
        </span>
        {onOpenInstanceDicom && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenInstanceDicom(instance);
            }}
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wide
                       bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800"
            title={t('strip.dicomClipHint')}
          >
            <FiFileText className="w-2.5 h-2.5" />
            {t('strip.dicomClip')}
          </button>
        )}
      </div>
      {/* Frame thumbnails */}
      <div className="flex gap-1.5 items-center">
        {isLoading ? (
          [...Array(Math.min(8, instance.frame_count))].map((_, i) => (
            <div
              key={i}
              className="flex-shrink-0 w-24 h-16 rounded-lg bg-slate-200 animate-pulse"
            />
          ))
        ) : (
          <>
            {frames.map((frame: any, idx: number) => (
              <FrameThumb
                key={frame.frame_id ?? idx}
                imageUrl={frame.image_url}
                frameNumber={idx}
                scrollRoot={scrollRoot}
                onClick={(e) => {
                  e.stopPropagation();
                  // Pass the frame's DB primary key (frame_id). It is unique per frame
                  // so the viewer can locate it in allFramesMeta with a direct find()
                  // — no index math, no SOP-UID collisions, no frame_number ambiguity.
                  onNavigateToFrame(
                    instance.sop_instance_uid,
                    String(frame.frame_id)
                  );
                }}
                onDicomClick={
                  onOpenInstanceDicom
                    ? () => onOpenInstanceDicom(instance)
                    : undefined
                }
              />
            ))}
            {remaining > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setVisibleCount((v) => v + FRAMES_PAGE);
                }}
                className="flex-shrink-0 w-24 h-16 rounded-lg border border-dashed border-slate-300
                             flex flex-col items-center justify-center gap-0.5 text-slate-500
                             hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 transition-all"
              >
                <span className="text-[11px] font-bold">+{remaining}</span>
                <span className="text-[9px]">more</span>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const ExamImageStrip: React.FC<{
  studyUID: string;
  /** sopInstanceUID = instance to open; frameId = DB primary key of the clicked frame */
  onNavigate: (sopInstanceUID?: string, frameId?: string) => void;
  onOpenDicom?: (payload: DicomMetadataPanelOpenPayload) => void;
}> = ({ studyUID, onNavigate, onOpenDicom }) => {
  const { t } = useTranslation('ExamManagement');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);

  const { data, isLoading } = useGetGeneralViewerInstances(
    studyUID,
    undefined,
    undefined,
    { enabled: true }
  );

  const instances: GeneralViewerInstance[] = useMemo(() => {
    const raw = (data as any)?.data?.data ?? (data as any)?.data ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [data]);

  // Expose the DOM node to children once it's mounted so IntersectionObserver gets the correct root
  const onScrollRef = useCallback((node: HTMLDivElement | null) => {
    (scrollRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    setScrollEl(node);
  }, []);

  const totalFrames = instances.reduce((s, i) => s + i.frame_count, 0);

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 px-6 py-4 border-t border-slate-100">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="flex-shrink-0 w-24 h-16 rounded-lg bg-slate-200 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (instances.length === 0) {
    return (
      <div className="px-6 py-4 border-t border-slate-100 text-slate-400 text-xs">
        {t('strip.noImages')}
      </div>
    );
  }

  return (
    /* overflow-hidden prevents this td from widening the table — only the inner div scrolls */
    <div
      className="border-t border-slate-100 overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-3 pb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            {t('strip.frames')}
          </span>
          <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px] font-bold border border-blue-100">
            {t('strip.clips', { count: instances.length })}
          </span>
          <span className="text-[10px] text-slate-400">
            {t('strip.totalFrames', {
              count: totalFrames.toLocaleString(
                (i18n.resolvedLanguage || i18n.language || 'en-US').startsWith(
                  'fr'
                )
                  ? 'fr-FR'
                  : 'en-US'
              ),
            })}
          </span>
        </div>
        <div
          className="flex items-center gap-2 shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <ViewerToolCluster
            studyUID={studyUID}
            variant="light"
            hideDicom={!onOpenDicom}
            onDicomClick={
              onOpenDicom
                ? () =>
                    onOpenDicom({
                      studyUID,
                      primarySeriesUid:
                        instances[0]?.serie_instance_uid ?? null,
                      initialTab: 'study',
                      selectedInstance: null,
                    })
                : undefined
            }
            dicomInstances={instances}
            liveExport={null}
            actionsIconOnly
            actionsIconGhost
            examExportMenu
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate();
            }}
            className="text-[10px] font-bold text-blue-600 hover:text-blue-800 hover:underline transition-colors"
          >
            {t('strip.openFullViewer')}
          </button>
        </div>
      </div>
      {/* Scrollable strip — only this div scrolls, table stays fixed */}
      <div
        ref={onScrollRef}
        className="flex gap-5 overflow-x-auto px-6 pb-4 items-start"
        style={{ scrollbarWidth: 'thin' }}
      >
        {instances.map((inst, idx) => (
          <React.Fragment key={inst.sop_instance_uid}>
            <InstanceFramesRow
              studyUID={studyUID}
              instance={inst}
              scrollRoot={scrollEl}
              onNavigateToFrame={(instanceSopUID, frameId) =>
                onNavigate(instanceSopUID, frameId)
              }
              onNavigate={() => onNavigate()}
              onOpenInstanceDicom={
                onOpenDicom
                  ? (instance) =>
                      onOpenDicom({
                        studyUID,
                        primarySeriesUid: instance.serie_instance_uid,
                        initialTab: 'image',
                        selectedInstance: instance,
                      })
                  : undefined
              }
            />
            {idx < instances.length - 1 && (
              <div className="flex-shrink-0 w-px self-stretch bg-slate-200 my-1" />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
// ─── End Exam Image Strip ─────────────────────────────────────────────────────

// --- Shared Exam List Types ---
type PredictionState = ExamListPredictionState;
type Series = ExamListSeries;
type Exam = ExamListItem;

const BLIND_SWEEP_TARGET = 7;

type ExamSeriesModality = 'BLIND' | 'OB' | 'REALTIME';

const MODALITY_COLUMN_KEYS: Record<
  ExamSeriesModality,
  | 'table.columns.modalityBlind'
  | 'table.columns.modalityOb'
  | 'table.columns.modalityRealtime'
> = {
  BLIND: 'table.columns.modalityBlind',
  OB: 'table.columns.modalityOb',
  REALTIME: 'table.columns.modalityRealtime',
};

/** One modality column (BLIND / OB / REALTIME): clear empty state + compact status card when present */
const ExamSeriesTypeCell: React.FC<{
  type: ExamSeriesModality;
  exam: Exam;
  pendingSeriePredictions: Set<string>;
  onSerieInfo: (uid: string) => void;
  onPredict: (studyUid: string, serieUid: string, isBlind: boolean) => void;
  t: TFunction;
}> = ({ type, exam, pendingSeriePredictions, onSerieInfo, onPredict, t }) => {
  const series = exam.serie?.filter((s) => s.type === type) ?? [];
  const isBlind = type === 'BLIND';

  if (series.length === 0) {
    return (
      <div
        className="flex min-h-[4.25rem] flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-800/45 px-2 py-2 text-center"
        title={t('table.series.emptyHint', {
          modality: t(MODALITY_COLUMN_KEYS[type]),
        })}
      >
        <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 leading-snug">
          {t('table.series.notIncluded')}
        </span>
      </div>
    );
  }

  return (
    <div className="flex min-h-[4.25rem] flex-col gap-2 py-0.5 justify-center">
      {series.map((s) => {
        const {
          predicted = 0,
          total = 0,
          failed = 0,
          inprogress = 0,
          blind_sweep_complete,
        } = s.prediction_state || {};
        const isPredicting =
          pendingSeriePredictions.has(s.serie_instance_uid) || inprogress > 0;
        const hasError = failed > 0;
        const isComplete = isBlind
          ? Boolean(blind_sweep_complete)
          : predicted === total && total > 0 && !hasError;
        const showPredictBtn = !isComplete && !isPredicting;

        const sweepCount = Math.min(total, BLIND_SWEEP_TARGET);

        return (
          <div
            key={s.serie_instance_uid}
            className="flex flex-col gap-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800/60 p-2 shadow-sm"
          >
            <div className="flex items-center justify-between gap-1">
              <div className="flex items-center gap-1.5 min-w-0">
                <div
                  className={cn(
                    'w-2 h-2 rounded-full flex-shrink-0',
                    isComplete
                      ? 'bg-emerald-500'
                      : hasError
                        ? 'bg-red-500 animate-pulse'
                        : 'bg-slate-300 dark:bg-slate-500'
                  )}
                />
                {isBlind ? (
                  <span
                    className="text-[11px] font-bold text-slate-700 dark:text-slate-200 tabular-nums truncate"
                    title={t('table.series.sweepsProgress', {
                      count: sweepCount,
                      target: BLIND_SWEEP_TARGET,
                    })}
                  >
                    {t('table.series.sweepsProgress', {
                      count: sweepCount,
                      target: BLIND_SWEEP_TARGET,
                    })}
                  </span>
                ) : (
                  <span className="text-[11px] font-bold text-slate-700 dark:text-slate-200 tabular-nums">
                    {predicted}/{total}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="flex-shrink-0 p-0.5 rounded text-slate-400 hover:text-blue-500 dark:hover:text-blue-400"
                title={t('filters.seriesUidTitle')}
                onClick={(e) => {
                  e.stopPropagation();
                  onSerieInfo(s.serie_instance_uid);
                }}
              >
                <FiInfo className="h-3.5 w-3.5" />
              </button>
            </div>

            {isPredicting ? (
              <div className="flex items-center justify-center gap-2 py-1.5 px-2 bg-blue-50 dark:bg-blue-900/25 text-blue-600 dark:text-blue-300 rounded-md border border-blue-100 dark:border-blue-800">
                <FiLoader className="h-3 w-3 animate-spin flex-shrink-0" />
                <span className="text-[10px] font-bold">
                  {t('table.series.predicting')}
                </span>
              </div>
            ) : showPredictBtn ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  onPredict(
                    exam.study_instance_uid,
                    s.serie_instance_uid,
                    isBlind
                  );
                }}
                className={cn(
                  'h-7 text-[10px] w-full',
                  hasError
                    ? 'text-red-600 border-red-100 hover:bg-red-50 dark:hover:bg-red-900/20'
                    : 'text-blue-600 border-blue-100 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                )}
              >
                {hasError ? (
                  <FiRefreshCw className="mr-1" />
                ) : (
                  <FiZap className="mr-1" />
                )}
                {hasError ? t('table.series.retry') : t('table.series.predict')}
              </Button>
            ) : isComplete ? (
              <div className="flex items-center justify-center gap-1 py-1.5 px-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300 rounded-md border border-emerald-100 dark:border-emerald-800">
                <FiCheckCircle className="h-3 w-3 flex-shrink-0" />
                <span className="text-[10px] font-bold">
                  {t('table.series.predicted')}
                </span>
              </div>
            ) : null}

            {hasError && !isPredicting && (
              <span className="text-[9px] text-red-500 text-center font-medium">
                {t('table.series.failedFrames')}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

// --- Main Page Component ---
const ExamManagement = () => {
  const { t } = useTranslation('ExamManagement');
  useDocumentTitle(`DEEPECHO - ${t('pageTitle')}`);
  const navigate = useNavigate();
  const { newInstanceNotification, clearNewInstanceNotification } =
    useGlobalWebSocket();

  // Add CSS animation for side panel
  React.useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      @keyframes slideInRight {
        from {
          transform: translateX(100%);
        }
        to {
          transform: translateX(0);
        }
      }
    `;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const [activeTab, setActiveTab] = useState<'Active' | 'Archived'>('Active');
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggleExpandRow = useCallback((uid: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }, []);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 7,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'study_date', desc: true },
  ]);
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(
    null
  );
  const [serieUidModal, setSerieUidModal] = useState<{
    open: boolean;
    uid: string;
  }>({
    open: false,
    uid: '',
  });

  const [dicomMetaPanel, setDicomMetaPanel] =
    useState<DicomMetadataPanelOpenPayload | null>(null);
  const [pendingSeriePredictions, setPendingSeriePredictions] = useState<
    Set<string>
  >(new Set());
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [isServerModalOpen, setIsServerModalOpen] = useState(false);
  const [selectedStudyForServer, setSelectedStudyForServer] = useState<
    string | null
  >(null);
  const [incompleteSessionsData, setIncompleteSessionsData] =
    useState<UploadSessionsByStudy | null>(null);
  const [incompleteModalOpen, setIncompleteModalOpen] = useState(false);
  const [selectedIncompleteSessionIndex, setSelectedIncompleteSessionIndex] =
    useState(0);

  // Column settings - load from localStorage or use defaults
  const defaultColumnOrder = [
    'study_date',
    'created_by',
    'patient_id',
    'patient_name',
    'birth_date_age',
    'lmp_date_ga',
    'blind_series',
    'ob_series',
    'realtime_series',
    'actions',
  ];

  const defaultColumnVisibility = {
    study_date: true,
    created_by: true,
    patient_id: true,
    patient_name: true,
    birth_date_age: true,
    lmp_date_ga: true,
    blind_series: true,
    ob_series: true,
    realtime_series: true,
    actions: true,
  };

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem('examTableColumnOrder');
    let parsed: string[] = saved ? JSON.parse(saved) : defaultColumnOrder;
    parsed = parsed.filter(
      (id) => id !== 'expand' && id !== 'study_instance_uid'
    );
    if (!parsed.includes('created_by')) {
      const idx = parsed.indexOf('study_date');
      if (idx >= 0) parsed.splice(idx + 1, 0, 'created_by');
      else parsed = [...defaultColumnOrder];
    }
    const missing = defaultColumnOrder.filter((id) => !parsed.includes(id));
    if (missing.length)
      parsed = [...parsed, ...missing.filter((id) => !parsed.includes(id))];
    return parsed;
  });

  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(() => {
    const saved = localStorage.getItem('examTableColumnVisibility');
    const merged = {
      ...defaultColumnVisibility,
      ...(saved ? JSON.parse(saved) : {}),
    };
    delete (merged as Record<string, boolean>).study_instance_uid;
    merged.created_by = merged.created_by !== false;
    return merged;
  });

  // Save to localStorage when settings change
  useEffect(() => {
    localStorage.setItem('examTableColumnOrder', JSON.stringify(columnOrder));
  }, [columnOrder]);

  useEffect(() => {
    localStorage.setItem(
      'examTableColumnVisibility',
      JSON.stringify(columnVisibility)
    );
  }, [columnVisibility]);

  // Track window resize for responsive columns
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const archiveExam = useArchiveExam();
  const unarchiveExam = useUnarchiveExam();
  const predictSerie = usePredictSerie();
  // Permission gating (UI only; backend enforces).
  const { can } = usePermissions();
  const canAcquire = can('study.acquire');
  const canArchive = can('study.archive');

  const debouncedSetColumnFilter = useDebouncedCallback(
    (column: string, value: any) => {
      setColumnFilters((prev) => ({ ...prev, [column]: value }));
    },
    350
  );

  const queryFilters = useMemo(() => {
    const textFilters: Record<string, string> = {};

    // Global search from filters panel
    if (filters.patient_name) {
      textFilters['patient_name__ilike'] = `%${filters.patient_name}%`;
      textFilters['patient_last_name__ilike'] = `%${filters.patient_name}%`;
    }

    // Column-specific search
    if (columnFilters.patient_name) {
      textFilters['patient_name__ilike'] = `%${columnFilters.patient_name}%`;
      textFilters['patient_last_name__ilike'] =
        `%${columnFilters.patient_name}%`;
    }
    if (columnFilters.patient_id) {
      textFilters['patient_id__eq'] = columnFilters.patient_id;
    }

    const dateFilters: Record<string, string> = {};
    if (dateRange.startDate)
      dateFilters['study_date__gte'] = dayjs(dateRange.startDate).format(
        'YYYY-MM-DD'
      );
    if (dateRange.endDate)
      dateFilters['study_date__lte'] = dayjs(dateRange.endDate).format(
        'YYYY-MM-DD'
      );

    const examFilters: Record<string, any> = {
      is_archived: activeTab === 'Archived',
      ...dateFilters,
    };

    // Column filter for Trimester (category)
    if (columnFilters.category && columnFilters.category.length > 0) {
      examFilters['category__in'] = columnFilters.category.join(',');
    }

    // Column filter for Serie UID by type
    if (columnFilters.blind_serie_uid) {
      examFilters['serie_instance_uid__ilike'] =
        `%${columnFilters.blind_serie_uid}%`;
      examFilters['serie_type__eq'] = 'BLIND';
    } else if (columnFilters.ob_serie_uid) {
      examFilters['serie_instance_uid__ilike'] =
        `%${columnFilters.ob_serie_uid}%`;
      examFilters['serie_type__eq'] = 'OB';
    } else if (columnFilters.realtime_serie_uid) {
      examFilters['serie_instance_uid__ilike'] =
        `%${columnFilters.realtime_serie_uid}%`;
      examFilters['serie_type__eq'] = 'REALTIME';
    }

    // Filter by serie type existence
    if (columnFilters.has_blind_serie) {
      examFilters['has_blind_serie'] = true;
    }
    if (columnFilters.has_ob_serie) {
      examFilters['has_ob_serie'] = true;
    }
    if (columnFilters.has_realtime_serie) {
      examFilters['has_realtime_serie'] = true;
    }

    const tableIdToServerSort: Record<string, string> = {
      study_date: 'created_at',
      created_by: 'creator_last_name',
      patient_id: 'patient_patient_id',
      patient_name: 'patient_patient_name',
      birth_date_age: 'patient_birth_date',
      lmp_date_ga: 'pregnancy_last_menstrual_period',
      blind_series: 'blind_series',
      ob_series: 'ob_series',
      realtime_series: 'realtime_series',
    };
    const firstSort = sorting[0];
    const sort_by =
      firstSort?.id && tableIdToServerSort[firstSort.id]
        ? tableIdToServerSort[firstSort.id]
        : 'created_at';
    const sort_order = firstSort ? (firstSort.desc ? 'desc' : 'asc') : 'desc';

    return {
      filters: {
        patient_filters:
          Object.keys(textFilters).length > 0
            ? { condition__or: textFilters }
            : {},
        exam_filters: examFilters,
      },
      sort_by,
      sort_order,
    };
  }, [filters, activeTab, dateRange, columnFilters, sorting]);

  const examsQuery = usePaginatedExams(
    pagination.pageIndex + 1,
    pagination.pageSize,
    queryFilters
  );
  const exams = examsQuery.data?.data ?? [];
  const examsTotalCount = examsQuery.data?.total_count ?? 0;

  useEffect(() => {
    if (newInstanceNotification) {
      const envelope = newInstanceNotification as {
        event?: string;
        data?: { data?: Record<string, unknown>; content?: string };
      };
      const instanceData = envelope.data?.data;
      if (instanceData) {
        const patientName = (instanceData.patient_name as string) || 'Unknown Patient';

        messageI18n.success('exam.newInstanceAdded', { patientName }, 4);

        examsQuery.refetch();
        clearNewInstanceNotification();
      }
    }
  }, [newInstanceNotification, clearNewInstanceNotification, examsQuery]);

  // Clear pending prediction state when refetched data shows the serie/study is complete (so "Predicting..." doesn't stick)
  useEffect(() => {
    if (exams.length === 0) return;
    setPendingSeriePredictions((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const exam of exams) {
        const series = exam.serie ?? [];
        for (const s of series) {
          if (!next.has(s.serie_instance_uid)) continue;
          const ps = (s.prediction_state || {}) as {
            predicted?: number;
            total?: number;
            failed?: number;
            blind_sweep_complete?: boolean;
          };
          const {
            predicted = 0,
            total = 0,
            failed = 0,
            blind_sweep_complete,
          } = ps;
          const isBlind = s.type === 'BLIND';
          const isComplete = isBlind
            ? Boolean(blind_sweep_complete)
            : predicted === total && total > 0 && !failed;
          if (isComplete) {
            next.delete(s.serie_instance_uid);
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [exams]);

  // Close filter dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveFilterColumn(null);
    if (activeFilterColumn) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeFilterColumn]);

  const debouncedFilterChange = useDebouncedCallback(
    (name: string, value: string) =>
      setFilters((prev) => ({ ...prev, [name]: value })),
    350
  );
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setDateRange((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const handleClearFilters = () => {
    setFilters({});
    setDateRange({ startDate: '', endDate: '' });
    const input = document.querySelector(
      'input[name="patient_name"]'
    ) as HTMLInputElement;
    if (input) input.value = '';
  };
  const handleTabClick = (tabName: 'Active' | 'Archived') => {
    setActiveTab(tabName);
    setPagination({ pageIndex: 0, pageSize: 7 });
  };

  const hasBlindSeries = (exam: Exam) =>
    exam.serie?.some((s: Series) => s.type === 'BLIND') ?? false;

  /** Total sweeps for a BLIND series (prediction_state.total). 7 = complete. */
  const blindSeriesTotal = (s: Series) =>
    (s.prediction_state?.total ?? 0) as number;

  /** True if any BLIND series has fewer than 7 sweeps → need to call session API to complete upload. */
  const hasBlindWithUnderSevenSweeps = (exam: Exam) =>
    exam.serie?.some(
      (s: Series) => s.type === 'BLIND' && blindSeriesTotal(s) < 7
    ) ?? false;

  /** True if at least one BLIND series in this exam is predicted (batch + BS complete). BS only. */
  const hasAtLeastOnePredictedBlindSeries = (exam: Exam) =>
    exam.serie?.some(
      (s: Series) =>
        s.type === 'BLIND' && Boolean(s.prediction_state?.blind_sweep_complete)
    ) ?? false;

  const handleOpenExamOrCompleteUpload = React.useCallback(
    async (exam: Exam) => {
      const studyUid = exam.study_instance_uid;
      // Session API only for BLIND with under 7 sweeps (incomplete). If total === 7, don't call.
      const shouldCheckSession =
        hasBlindSeries(exam) && hasBlindWithUnderSevenSweeps(exam);
      if (shouldCheckSession) {
        try {
          const data = await getUploadSessionByStudy(studyUid);
          if (data?.sessions?.length) {
            if (hasAtLeastOnePredictedBlindSeries(exam)) {
              navigate(`/exam/viewer?StudyInstanceUIDs=${studyUid}`);
              return;
            }
            setIncompleteSessionsData(data);
            setSelectedIncompleteSessionIndex(0);
            setIncompleteModalOpen(true);
            return;
          }
        } catch {
          // 404 or error => no incomplete BS session for this study
        }
      }
      navigate(`/exam/viewer?StudyInstanceUIDs=${studyUid}`);
    },
    [navigate]
  );

  const handleRefetch = async () => {
    try {
      await examsQuery.refetch();
    } catch (error) {
      console.error('Error during refetch:', error);
    }
  };

  const handleOperation = async (
    studyInstanceUid: string,
    operation: 'archive' | 'unarchive'
  ) => {
    try {
      await (operation === 'archive' ? archiveExam : unarchiveExam).mutateAsync(
        studyInstanceUid
      );
      await handleRefetch();
    } catch (error) {
      messageI18n.error('exam.operationFailed', { operation });
    }
  };

  const generateSeriesSRHandler = async (
    studyInstanceUid: string,
    serieInstanceUid: string,
    isBlindSeries?: boolean
  ) => {
    // BS only: if exam is incomplete (missing sweeps), show complete modal instead of predicting. OB is not concerned.
    if (isBlindSeries) {
      try {
        const data = await getUploadSessionByStudy(studyInstanceUid);
        if (data?.sessions?.length) {
          setIncompleteSessionsData(data);
          setSelectedIncompleteSessionIndex(0);
          setIncompleteModalOpen(true);
          return;
        }
      } catch {
        // 404 => no incomplete session, proceed with prediction
      }
    }
    setPendingSeriePredictions((prev) => new Set(prev).add(serieInstanceUid));
    try {
      await predictSerie.mutateAsync({
        prediction_type: 'SERIE',
        study_uid: studyInstanceUid,
        serie_uid: serieInstanceUid,
      });
      messageI18n.success('prediction.startedForSeries');
      await handleRefetch();
      // Keep serie in pendingSeriePredictions so "Predicting..." stays until refetch shows inprogress or completion
    } catch (error) {
      setPendingSeriePredictions((prev) => {
        const next = new Set(prev);
        next.delete(serieInstanceUid);
        return next;
      });
      const errMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? '';
      if (
        isBlindSeries &&
        (errMessage.includes('7 sweeps') ||
          errMessage.includes('complete the upload'))
      ) {
        try {
          const data = await getUploadSessionByStudy(studyInstanceUid);
          if (data?.sessions?.length) {
            setIncompleteSessionsData(data);
            setSelectedIncompleteSessionIndex(0);
            setIncompleteModalOpen(true);
          } else {
            messageI18n.error('prediction.seriesFailed');
          }
        } catch {
          messageI18n.error('prediction.seriesFailed');
        }
      } else {
        messageI18n.error('prediction.seriesFailed');
      }
    }
  };

  const handleServerSelection = (server: any) => {
    console.log('server', server);
  };

  const TABS = [
    { id: 'Active' as const, labelKey: 'tabs.active', icon: FiCheckCircle },
    { id: 'Archived' as const, labelKey: 'tabs.archived', icon: FiArchive },
  ];

  // Header filter components
  const SearchHeaderFilter = ({
    column,
    placeholder,
  }: {
    column: string;
    placeholder: string;
  }) => {
    const isActive = activeFilterColumn === column;
    const filterValue = columnFilters[column] || '';

    return (
      <div
        className="relative inline-block"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveFilterColumn(isActive ? null : column);
          }}
          className={cn(
            'ml-2 p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700',
            (isActive || filterValue) &&
              'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300'
          )}
          title={t('filters.searchTitle')}
        >
          <FiSearch className="h-3 w-3" />
        </button>
        {isActive && (
          <div
            className="absolute top-full left-0 mt-1 bg-card-color-100 dark:bg-gray-800 border border-slate-200 dark:border-slate-700 rounded shadow-lg p-2 z-50 min-w-[200px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-1">
              <Input
                placeholder={placeholder}
                value={filterValue}
                onChange={(e) => {
                  const newValue = e.target.value;
                  // Update immediately for responsive UI
                  setColumnFilters((prev) => ({ ...prev, [column]: newValue }));
                  // Also trigger debounced update for backend filters
                  debouncedSetColumnFilter(column, newValue);
                }}
                className="text-xs"
                autoFocus
              />
              {filterValue && (
                <button
                  onClick={() => {
                    setColumnFilters((prev) => {
                      const newFilters = { ...prev };
                      delete newFilters[column];
                      return newFilters;
                    });
                  }}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <FiX className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const FilterHeaderDropdown = ({
    column,
    options,
  }: {
    column: string;
    options: string[];
  }) => {
    const isActive = activeFilterColumn === column;
    const filterValue = columnFilters[column] || [];

    return (
      <div
        className="relative inline-block"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveFilterColumn(isActive ? null : column);
          }}
          className={cn(
            'ml-2 p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700',
            (isActive || filterValue.length > 0) &&
              'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300'
          )}
          title={t('filters.filterTitle')}
        >
          <FiFilter className="h-3 w-3" />
        </button>
        {isActive && (
          <div
            className="absolute top-full left-0 mt-1 bg-card-color-100 dark:bg-gray-800 border border-slate-200 dark:border-slate-700 rounded shadow-lg p-2 z-50 min-w-[150px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-1">
              {options.map((option) => (
                <label
                  key={option}
                  className="flex items-center gap-2 p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={filterValue.includes(option)}
                    onChange={(e) => {
                      const newValue = e.target.checked
                        ? [...filterValue, option]
                        : filterValue.filter((v: string) => v !== option);
                      setColumnFilters((prev) => ({
                        ...prev,
                        [column]: newValue,
                      }));
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">{option}</span>
                </label>
              ))}
            </div>
            {filterValue.length > 0 && (
              <button
                onClick={() => {
                  setColumnFilters((prev) => {
                    const newFilters = { ...prev };
                    delete newFilters[column];
                    return newFilters;
                  });
                }}
                className="mt-2 text-xs text-slate-500 hover:text-slate-700 w-full text-center"
              >
                {t('filters.clearFilter')}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const DateRangeHeaderFilter = ({ column }: { column: string }) => {
    const isActive = activeFilterColumn === column;
    const filterValue = columnFilters[column] || { start: '', end: '' };

    return (
      <div
        className="relative inline-block"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveFilterColumn(isActive ? null : column);
          }}
          className={cn(
            'ml-2 p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700',
            (isActive || filterValue.start || filterValue.end) &&
              'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300'
          )}
          title={t('filters.dateRangeTitle')}
        >
          <FiFilter className="h-3 w-3" />
        </button>
        {isActive && (
          <div
            className="absolute top-full left-0 mt-1 bg-card-color-100 dark:bg-gray-800 border border-slate-200 dark:border-slate-700 rounded shadow-lg p-3 z-50 min-w-[240px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-2">
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-400 mb-1 block">
                  From:
                </label>
                <input
                  type="date"
                  value={filterValue.start}
                  onChange={(e) => {
                    setColumnFilters((prev) => ({
                      ...prev,
                      [column]: { ...filterValue, start: e.target.value },
                    }));
                  }}
                  className="w-full px-2 py-1 text-xs border rounded dark:bg-slate-700 dark:border-slate-600"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-400 mb-1 block">
                  To:
                </label>
                <input
                  type="date"
                  value={filterValue.end}
                  onChange={(e) => {
                    setColumnFilters((prev) => ({
                      ...prev,
                      [column]: { ...filterValue, end: e.target.value },
                    }));
                  }}
                  className="w-full px-2 py-1 text-xs border rounded dark:bg-slate-700 dark:border-slate-600"
                />
              </div>
            </div>
            {(filterValue.start || filterValue.end) && (
              <button
                onClick={() => {
                  setColumnFilters((prev) => {
                    const newFilters = { ...prev };
                    delete newFilters[column];
                    return newFilters;
                  });
                }}
                className="mt-2 text-xs text-slate-500 hover:text-slate-700 w-full text-center"
              >
                {t('filters.clearFilter')}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Column Settings Panel Component (Side Panel)
  const ColumnSettingsPanel = () => {
    const columnLabels: Record<string, string> = {
      study_date: t('table.columns.examDateTime'),
      created_by: t('table.columns.createdBy'),
      patient_id: t('table.columns.patientId'),
      patient_name: t('table.columns.patientName'),
      birth_date_age: t('table.columns.birthDateAge'),
      lmp_date_ga: t('table.columns.lmpGaTrimester'),
      blind_series: t('table.columns.blindSeries'),
      ob_series: t('table.columns.obSeries'),
      realtime_series: t('table.columns.realtimeSeries'),
      actions: t('table.columns.actions'),
    };

    const handleDragStart = (
      e: React.DragEvent<HTMLDivElement>,
      columnId: string
    ) => {
      setDraggedItem(columnId);
      e.dataTransfer.effectAllowed = 'move';
      e.currentTarget.style.opacity = '0.5';
    };

    const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
      e.currentTarget.style.opacity = '1';
      setDraggedItem(null);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (
      e: React.DragEvent<HTMLDivElement>,
      targetColumnId: string
    ) => {
      e.preventDefault();

      if (!draggedItem || draggedItem === targetColumnId) return;

      const newOrder = [...columnOrder];
      const draggedIndex = newOrder.indexOf(draggedItem);
      const targetIndex = newOrder.indexOf(targetColumnId);

      // Remove dragged item and insert at target position
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedItem);

      setColumnOrder(newOrder);
    };

    const toggleColumnVisibility = (columnId: string) => {
      // Don't allow hiding actions column on desktop
      if (columnId === 'actions' && windowWidth >= 768) {
        messageI18n.warning('exam.actionsColumnCannotHide');
        return;
      }
      setColumnVisibility((prev) => ({
        ...prev,
        [columnId]: !prev[columnId],
      }));
    };

    const resetToDefaults = () => {
      setColumnOrder(defaultColumnOrder);
      setColumnVisibility(defaultColumnVisibility);
      messageI18n.success('exam.columnSettingsReset');
    };

    if (!showColumnSettings) return null;

    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 transition-opacity duration-300"
          onClick={() => setShowColumnSettings(false)}
        />

        {/* Side Panel */}
        <div
          className="fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-white dark:bg-gray-800 shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-in-out"
          style={{ animation: 'slideInRight 0.3s ease-out' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
            <div>
              <h3 className="text-lg font-semibold text-title-page-color-100">
                {t('columnSettings.title')}
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {t('columnSettings.subtitle')}
              </p>
            </div>
            <button
              onClick={() => setShowColumnSettings(false)}
              className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              title={t('filters.close')}
            >
              <FiX className="h-5 w-5" />
            </button>
          </div>

          {/* Column List */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-2">
              {columnOrder.map((columnId, index) => (
                <div
                  key={columnId}
                  draggable
                  onDragStart={(e) => handleDragStart(e, columnId)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, columnId)}
                  className={cn(
                    'flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg cursor-move transition-all hover:shadow-md hover:bg-slate-100 dark:hover:bg-slate-700',
                    draggedItem === columnId && 'opacity-50'
                  )}
                >
                  {/* Drag Handle */}
                  <div className="text-slate-400 dark:text-slate-500">
                    <svg
                      className="h-5 w-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
                    </svg>
                  </div>

                  {/* Checkbox and Label */}
                  <label
                    className="flex items-center gap-3 flex-1 cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={columnVisibility[columnId] ?? true}
                      onChange={() => toggleColumnVisibility(columnId)}
                      className="w-4 h-4 rounded border-slate-300 text-primary-main focus:ring-2 focus:ring-primary-main"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-title-page-color-100">
                        {columnLabels[columnId] || columnId}
                      </span>
                      {columnId === 'actions' && windowWidth >= 768 && (
                        <span className="ml-2 text-xs text-slate-500">
                          {t('columnSettings.required')}
                        </span>
                      )}
                    </div>
                  </label>

                  {/* Order Number */}
                  <span className="text-xs font-semibold text-slate-400 dark:text-slate-500 w-6 text-center">
                    {index + 1}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
            <Button onClick={resetToDefaults} variant="secondary" size="sm">
              <FiRefreshCw className="mr-2 h-4 w-4" />
              {t('columnSettings.reset')}
            </Button>
            <Button onClick={() => setShowColumnSettings(false)} size="sm">
              <FiCheckCircle className="mr-2 h-4 w-4" />
              {t('columnSettings.done')}
            </Button>
          </div>
        </div>
      </>
    );
  };

  const columns = useMemo<ColumnDef<Exam>[]>(
    () => [
      {
        id: 'study_date',
        header: t('table.columns.examDateTime'),
        enableSorting: true,
        // accessorFn required so getCanSort() is true under manualSorting (server sorts; UI only toggles state)
        accessorFn: (row: Exam) => row.created_at ?? row.study_date ?? '',
        cell: ({ row }) => formatExamDateTime(row.original),
      },
      {
        id: 'created_by',
        header: t('table.columns.createdBy'),
        enableSorting: true,
        accessorFn: (row: Exam) =>
          row.creator?.last_name ?? row.creator?.email ?? '',
        cell: ({ row }) => (
          <div className="flex flex-col gap-0.5 max-w-[160px]">
            <span
              className="text-xs font-medium text-slate-800 dark:text-slate-100 truncate"
              title={formatCreatorLabel(row.original.creator)}
            >
              {formatCreatorLabel(row.original.creator)}
            </span>
            {row.original.creator?.email &&
              formatCreatorLabel(row.original.creator) !==
                row.original.creator.email && (
                <span
                  className="text-[10px] text-slate-500 truncate"
                  title={row.original.creator.email}
                >
                  {row.original.creator.email}
                </span>
              )}
          </div>
        ),
      },
      {
        id: 'patient_id',
        header: t('table.columns.patientId'),
        enableSorting: true,
        accessorFn: (row: Exam) => String(row.patient?.patient_id ?? ''),
        cell: ({ row }) => (
          <span className="text-xs font-bold text-slate-600">
            {row.original.patient?.patient_id || t('common.notAvailable')}
          </span>
        ),
      },
      {
        id: 'patient_name',
        header: t('table.columns.patientName'),
        enableSorting: true,
        accessorFn: (row: Exam) =>
          `${row.patient?.patient_name ?? ''} ${row.patient?.patient_last_name ?? ''}`.trim(),
        cell: ({ row }) => (
          <span className="text-xs font-bold text-blue-900 truncate block max-w-[120px]">
            {row.original.patient?.patient_name}{' '}
            {row.original.patient?.patient_last_name}
          </span>
        ),
      },
      {
        id: 'birth_date_age',
        header: t('table.columns.birthDateAge'),
        enableSorting: true,
        accessorFn: (row: Exam) => row.patient?.birth_date ?? '',
        cell: ({ row }) => {
          const bd = row.original.patient?.birth_date;
          if (!bd) {
            return (
              <span className="text-xs text-slate-400 dark:text-slate-500">
                —
              </span>
            );
          }
          return (
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-xs font-medium text-slate-800 dark:text-slate-100">
                {formatDate(bd)}
              </span>
              <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 tabular-nums">
                {calculateAge(bd)}
              </span>
            </div>
          );
        },
      },
      {
        id: 'lmp_date_ga',
        header: t('table.columns.lmpGa'),
        enableSorting: true,
        accessorFn: (row: Exam) => row.pregnancy?.last_menstrual_period ?? '',
        cell: ({ row }) => {
          const preg = row.original.pregnancy;
          const weeks = preg?.gestational_age_weeks;
          const days = preg?.gestational_age_days;
          const lmpRaw = preg?.last_menstrual_period;
          const lmpStr = lmpRaw
            ? parseFlexibleDate(lmpRaw).format('YYYY-MM-DD')
            : '';

          const wNum = weeks != null ? Number(weeks) : NaN;
          const dNum = days != null ? Number(days) : 0;
          const hasApiGa = !Number.isNaN(wNum) && (wNum > 0 || dNum > 0);

          let gaDisplay: string | null = hasApiGa
            ? formatGestationalAge(wNum, Number.isNaN(dNum) ? 0 : dNum)
            : null;

          if (!gaDisplay && lmpStr && parseFlexibleDate(lmpRaw).isValid()) {
            const ref =
              (preg?.ultrasound_date &&
                parseFlexibleDate(preg.ultrasound_date).isValid() &&
                parseFlexibleDate(preg.ultrasound_date).format('YYYY-MM-DD')) ||
              (parseFlexibleDate(row.original.study_date).isValid() &&
                parseFlexibleDate(row.original.study_date).format(
                  'YYYY-MM-DD'
                )) ||
              (row.original.created_at
                ? dayjs(row.original.created_at).format('YYYY-MM-DD')
                : undefined);
            const computed = computeGAFromLmp(lmpStr, ref);
            if (computed) {
              gaDisplay = formatGestationalAge(computed.weeks, computed.days);
            }
          }

          return gaDisplay ? (
            <div className="flex flex-col gap-1">
              <span className="text-[10px] text-slate-500">
                {formatDate(lmpRaw ?? undefined)}
              </span>
              <div className="flex gap-1 flex-wrap">
                <span className="px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800 rounded text-[10px] font-bold">
                  {gaDisplay}
                </span>
                {row.original.category &&
                  row.original.category !== 'NOT_DEFINED' && (
                    <span className="px-1.5 py-0.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-100 dark:border-purple-800 rounded text-[10px] font-bold">
                      {row.original.category}
                    </span>
                  )}
              </div>
            </div>
          ) : (
            <span className="text-slate-300">-</span>
          );
        },
      },
      ...(['BLIND', 'OB', 'REALTIME'] as ExamSeriesModality[]).map((type) => ({
        id: `${type.toLowerCase()}_series`,
        header: t(MODALITY_COLUMN_KEYS[type]),
        enableSorting: true,
        accessorFn: (row: Exam) =>
          (row.serie ?? []).filter((s) => s.type === type).length,
        cell: ({ row }: { row: { original: Exam } }) => (
          <ExamSeriesTypeCell
            type={type}
            exam={row.original}
            pendingSeriePredictions={pendingSeriePredictions}
            onSerieInfo={(uid) => setSerieUidModal({ open: true, uid })}
            onPredict={(studyUid, serieUid, isBlind) =>
              generateSeriesSRHandler(studyUid, serieUid, isBlind)
            }
            t={t}
          />
        ),
      })),
      {
        id: 'actions',
        header: t('table.columns.actions'),
        enableSorting: false,
        cell: ({ row }) => (
          <div
            className="flex w-full min-w-0 flex-nowrap items-center justify-end gap-1 whitespace-nowrap"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={`${EXAM_ICON_HIT} text-blue-600 hover:bg-blue-50/90 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-blue-950/40 dark:hover:text-blue-300`}
              title={t('table.actions.openReport')}
              aria-label={t('table.actions.openReport')}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenExamOrCompleteUpload(row.original);
              }}
            >
              <FiCpu className="h-[18px] w-[18px]" />
            </button>
            <ExamRowDicomExportIcons
              studyUID={(row.original as Exam).study_instance_uid}
              onOpenStudyDicom={() => {
                const exam = row.original as Exam;
                const primary = exam.serie?.[0]?.serie_instance_uid ?? null;
                setDicomMetaPanel({
                  studyUID: exam.study_instance_uid,
                  primarySeriesUid: primary,
                  initialTab: 'study',
                  selectedInstance: null,
                });
              }}
            />
            <button
              type="button"
              className={`${EXAM_ICON_HIT} text-violet-600 hover:bg-violet-50/90 hover:text-violet-700 dark:text-violet-400 dark:hover:bg-violet-950/40 dark:hover:text-violet-300`}
              title={t('table.actions.openEndpoints')}
              aria-label={t('table.actions.openEndpoints')}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStudyForServer(row.original.study_instance_uid);
                setIsServerModalOpen(true);
              }}
            >
              <FiSend className="h-[18px] w-[18px]" />
            </button>
            {canArchive && (
            <button
              type="button"
              title={
                activeTab === 'Active'
                  ? t('table.actions.archive')
                  : t('table.actions.unarchive')
              }
              aria-label={
                activeTab === 'Active'
                  ? t('table.actions.archive')
                  : t('table.actions.unarchive')
              }
              onClick={(e) => {
                e.stopPropagation();
                handleOperation(
                  row.original.study_instance_uid,
                  activeTab === 'Active' ? 'archive' : 'unarchive'
                );
              }}
              className={cn(
                EXAM_ICON_HIT,
                activeTab === 'Active'
                  ? 'text-amber-600 hover:bg-amber-50/90 hover:text-amber-700 dark:text-amber-400 dark:hover:bg-amber-950/35 dark:hover:text-amber-300'
                  : 'text-emerald-600 hover:bg-emerald-50/90 hover:text-emerald-700 dark:text-emerald-400 dark:hover:bg-emerald-950/35 dark:hover:text-emerald-300'
              )}
            >
              {activeTab === 'Active' ? (
                <FiArchive className="h-[18px] w-[18px]" />
              ) : (
                <FiInbox className="h-[18px] w-[18px]" />
              )}
            </button>
            )}
          </div>
        ),
      },
    ],
    [
      activeTab,
      pendingSeriePredictions,
      activeFilterColumn,
      columnFilters,
      t,
      generateSeriesSRHandler,
      setDicomMetaPanel,
      canArchive,
    ]
  );

  // Apply column visibility and ordering
  const visibleColumns = useMemo(() => {
    // Filter columns based on visibility settings
    const columnsMap = new Map(columns.map((col) => [col.id, col]));

    // Reorder based on columnOrder and filter out hidden columns
    const orderedVisible = columnOrder
      .filter((id) => columnVisibility[id] !== false && columnsMap.has(id))
      .map((id) => columnsMap.get(id)!)
      .filter(Boolean);

    // Add responsive sizing - hide some columns on mobile
    if (windowWidth < 768) {
      return orderedVisible.filter((col) => {
        const essentialColumns = [
          'patient_name',
          'birth_date_age',
          'study_date',
          'created_by',
          'lmp_date_ga',
          'blind_series',
          'ob_series',
          'realtime_series',
        ];
        return essentialColumns.includes(col.id as string);
      });
    }

    // On tablet, show a medium set of columns
    if (windowWidth < 1024) {
      return orderedVisible.filter((col) => {
        const tabletColumns = [
          'study_date',
          'created_by',
          'patient_name',
          'birth_date_age',
          'lmp_date_ga',
          'blind_series',
          'ob_series',
          'realtime_series',
          'actions',
        ];
        return tabletColumns.includes(col.id as string);
      });
    }

    // Desktop: show all visible columns
    return orderedVisible;
  }, [columns, columnOrder, columnVisibility, windowWidth]);

  const table = useReactTable({
    data: exams,
    columns: visibleColumns,
    state: {
      pagination,
      sorting,
    },
    onPaginationChange: setPagination,
    onSortingChange: (updater) => {
      setSorting(updater);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    getRowId: (row) => row.study_instance_uid,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    enableSortingRemoval: false,
    rowCount: examsTotalCount,
  });

  const isLoading = examsQuery.isPending && examsQuery.data === undefined;

  return (
    <div className="bg-card-color-200 dark:bg-gray-900 p-6">
      <div className="max-w-full mx-auto space-y-6">
        <div
          className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4`}
        >
          <div>
            <h1 className="text-2xl font-bold text-title-page-color-100 dark:text-white">
              {t('pageTitle')}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('pageSubtitle')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setFiltersVisible(!filtersVisible)}
            >
              <FiFilter className="mr-2 h-4 w-4" /> {t('buttons.filters')}
            </Button>
            {canAcquire && <NewCaseReverseUpload />} {/* study.acquire */}
          </div>
        </div>

        {/* Filters */}
        {filtersVisible && (
          <div className="bg-card-color-100 dark:bg-gray-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
            <div className="flex flex-wrap items-end gap-4 w-full">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-medium text-title-page-color-300 dark:text-gray-400 mb-1 block">
                  {t('filters.patientName')}
                </label>
                <Input
                  placeholder={t('filters.patientNamePlaceholder')}
                  name="patient_name"
                  onChange={(e) =>
                    debouncedFilterChange(e.target.name, e.target.value)
                  }
                />
              </div>
              <div className="min-w-[150px]">
                <label className="text-xs font-medium text-title-page-color-300 dark:text-gray-400 mb-1 block">
                  {t('filters.startDate')}
                </label>
                <DateInput
                  name="startDate"
                  value={dateRange.startDate}
                  onChange={handleDateChange}
                />
              </div>
              <div className="min-w-[150px]">
                <label className="text-xs font-medium text-title-page-color-300 dark:text-gray-400 mb-1 block">
                  {t('filters.endDate')}
                </label>
                <DateInput
                  name="endDate"
                  value={dateRange.endDate}
                  onChange={handleDateChange}
                  min={dateRange.startDate}
                />
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                  {t('filters.clearAll')}
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-card-color-100 dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="px-6 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
            <nav className="flex space-x-6">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab.id)}
                    className={cn(
                      'flex items-center gap-2 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors',
                      activeTab === tab.id
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                    )}
                  >
                    <Icon
                      className={cn(
                        'w-4 h-4',
                        activeTab === tab.id
                          ? 'text-blue-600'
                          : 'text-slate-400 dark:text-slate-500'
                      )}
                    />
                    {t(tab.labelKey)}
                  </button>
                );
              })}
            </nav>

            {/* Settings Button */}
            <button
              onClick={() => setShowColumnSettings(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              title={t('filters.tableSettings')}
            >
              <FiSettings className="h-4 w-4" />
              <span className="hidden sm:inline">{t('filters.tableSettings')}</span>
            </button>
          </div>

          <Table
            table={table}
            isLoading={isLoading}
            loadingMessage={t('table.loadingExams')}
            isFetching={examsQuery.isFetching}
            columnsLength={table.getAllColumns().length}
            onRowClick={(exam) =>
              toggleExpandRow((exam as Exam).study_instance_uid)
            }
            isRowExpanded={(row) =>
              expandedRows.has((row.original as Exam).study_instance_uid)
            }
            renderSubComponent={({ row }) => (
              <ExamImageStrip
                studyUID={(row.original as Exam).study_instance_uid}
                onNavigate={(sopInstanceUID?: string, frameId?: string) => {
                  const exam = row.original as Exam;
                  if (sopInstanceUID && frameId) {
                    const params = new URLSearchParams({
                      StudyInstanceUIDs: exam.study_instance_uid,
                      sopInstanceUID,
                      frameId,
                    });
                    navigate(`/exam/viewer?${params.toString()}`);
                  } else {
                    handleOpenExamOrCompleteUpload(exam);
                  }
                }}
                onOpenDicom={setDicomMetaPanel}
              />
            )}
          />
        </div>
      </div>

      <GeneralViewerDicomMetadataPanel
        open={dicomMetaPanel !== null}
        onClose={() => setDicomMetaPanel(null)}
        studyUID={dicomMetaPanel?.studyUID ?? ''}
        primarySeriesUid={dicomMetaPanel?.primarySeriesUid ?? null}
        selectedInstance={dicomMetaPanel?.selectedInstance ?? null}
        initialTab={dicomMetaPanel?.initialTab ?? 'study'}
      />

      {/* Serie UID Modal */}
      {serieUidModal.open && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setSerieUidModal({ open: false, uid: '' })}
        >
          <div
            className="bg-card-color-100 dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-2xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-title-page-color-100 dark:text-white">
                Serie Instance UID
              </h3>
              <button
                onClick={() => setSerieUidModal({ open: false, uid: '' })}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              >
                ✕
              </button>
            </div>
            <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded border border-slate-200 dark:border-slate-700">
              <code className="text-sm break-all text-slate-700 dark:text-slate-300">
                {serieUidModal.uid}
              </code>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setSerieUidModal({ open: false, uid: '' })}
              >
                Close
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  copyToClipboard(serieUidModal.uid);
                  setSerieUidModal({ open: false, uid: '' });
                }}
              >
                <FiCopy className="mr-2" />
                Copy to Clipboard
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Column Settings Panel */}
      <ColumnSettingsPanel />

      {/* Server Selection Modal */}
      <ServerSelectionModal
        isOpen={isServerModalOpen}
        onClose={() => {
          setIsServerModalOpen(false);
          setSelectedStudyForServer(null);
        }}
        onSelectServer={handleServerSelection}
        studyInstanceUid={selectedStudyForServer || undefined}
      />

      {/* Complete incomplete BS upload (one session per BLIND series; complete one to get access to viewer) */}
      <CompleteIncompleteUploadModal
        isOpen={incompleteModalOpen}
        onClose={() => {
          setIncompleteModalOpen(false);
          setIncompleteSessionsData(null);
        }}
        onSuccess={() => {
          examsQuery.refetch();
          setIncompleteSessionsData(null);
          setIncompleteModalOpen(false);
          navigate('/exams');
        }}
        session={
          incompleteSessionsData?.sessions?.[selectedIncompleteSessionIndex]
            ? {
                upload_session_id:
                  incompleteSessionsData.sessions[
                    selectedIncompleteSessionIndex
                  ].upload_session_id,
                study_instance_uid: incompleteSessionsData.study_instance_uid,
                missing_keys:
                  incompleteSessionsData.sessions[
                    selectedIncompleteSessionIndex
                  ].missing_keys,
                study_category:
                  incompleteSessionsData.sessions[
                    selectedIncompleteSessionIndex
                  ].study_category,
              }
            : null
        }
        allSessions={incompleteSessionsData?.sessions ?? []}
        selectedIndex={selectedIncompleteSessionIndex}
        onSelectSession={setSelectedIncompleteSessionIndex}
      />
    </div>
  );
};

export default ExamManagement;
